import React from 'react';
import { Text } from '@fluentui/react/lib/Text';
import {
  Icon,
  Persona,
  PersonaSize,
  PrimaryButton,
  DefaultButton,
  IColumn,
  IDetailsListProps,
  DetailsRow,
  IComboBoxOption,
  DetailsList,
  SelectionMode,
  DetailsListLayoutMode,
  IDropdownOption,
} from '@fluentui/react';

import TimeTaxSupportSystemClient from '../../../system';
import MessageCourier from '../../../lib/MessageCourier';
import { ModalCannotModifyDocs } from './ModalCannotModifyDocs';
import { ProjectConfigurationDocsForm, ProjectDocumentControlForm } from './ProjectConfigurationDocsForm';
import DropdownInput from '../../../lib/FormValidator/Form/DropdownInput';
import DocumentUploadControl from '../../../system/documents/DocumentUploadControl';
import CustomInputIfc from '../../../lib/FormValidator/Form/CustomInputIfc';
import SystemDocumentType from '../../../system/documents/SystemDocumentType';
import CollaboratorTeam from '../../../system/collaborators/collaborator_team/CollaboratorTeam';
import InputField from '../../../lib/FormValidator/Form/InputField';
import { DatePickerInput } from '../../../lib/FormValidator/Form/DatePickerInput';
import { DateTime } from 'luxon';
import { useTranslation, Trans } from 'react-i18next';
import ProjectConfigurationForm from './ProjectConfigurationForm';
import { ITemporality } from '../../../system/temporality/TemporalitySystem';
import { ItipoConcepto } from '../../../system/projects/ProjectsSystem';
import { getPeriodNameInRange } from '../../../common/generatePeriods';

interface IProjectConfigurationDocsProps {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  documentControlForm: ProjectConfigurationDocsForm;
  onBlurCustomInput: (customInput: CustomInputIfc) => void;
  documentTypeList: SystemDocumentType[];
  temporalities: ITemporality[];
  collaboratorsInTeam: CollaboratorTeam[];
  formProject: ProjectConfigurationForm;
  selectedtipoConsep: ItipoConcepto;
}

export const ProjectConfigurationDocs = (props: IProjectConfigurationDocsProps) => {
  const { t, i18n } = useTranslation();

  const [editing, setEditing] = React.useState<boolean>(false);
  const [formDocIsValid, setFormDocIsValid] = React.useState(false);

  const [items, setItems] = React.useState<{
      uploaded: boolean;
      expired: boolean;
      concept: string;
      documentTypeName: string;
      limitDate: string;
      responsible: string;
      fromDate: string;
      temporality: string | undefined;
      typeTemporality: string | undefined;
  }[]>([]);

  const [documentControlSelected, setDocumentControlSelected] = React.useState<
      DocumentUploadControl | undefined
  >(undefined);

  const [showCannotModifyDoc, setShowCannotModifyDoc] = React.useState<boolean>(false);
  const [isBlurTemporality, setIsBlurTemporality] = React.useState<boolean>(false);
  
  const [currentForm, setCurrentForm] = React.useState<ProjectDocumentControlForm>(
    props.documentControlForm.initializeItemForm()
  );
  
  const isService = props.selectedtipoConsep.codigo.toLocaleUpperCase() === "SRC"
  const temporabilityInput = currentForm.getTemporalityInput();
  
  const isInvalidTemporability = items.some((item) => !item.temporality && isService);

  const onSave = () => {
    if (currentForm.isValid()) {
      if (!documentControlSelected) {
        props.documentControlForm.add(currentForm.buildDocumentControl());
      } else {
        props.documentControlForm.replace(currentForm.buildDocumentControl(), documentControlSelected);
      }

      const newList = props.documentControlForm.getValue();
      setItems(newList as any[]);
      setCurrentForm(props.documentControlForm.initializeItemForm());
      setEditing(false);
      setFormDocIsValid(false);
    } else {
      const isValid = isService ? currentForm.isValid() && temporabilityInput.getValue() !== undefined : currentForm.isValid();
      setFormDocIsValid(isValid);
    }
    getItems();
  };

  React.useEffect(() => {
    getItems();
  }, [props.documentControlForm]);

  const onEditing = (documentControlSelected: DocumentUploadControl | undefined) => {
    setEditing(true);
    setDocumentControlSelected(documentControlSelected);
    if (documentControlSelected) {
      setCurrentForm(props.documentControlForm.forItem(documentControlSelected));
      const isValid = isService ? currentForm.isValid() && temporabilityInput.getValue() !== undefined : currentForm.isValid();
      setFormDocIsValid(isValid);
    } else {
      setCurrentForm(props.documentControlForm.initializeItemForm());
    }
    getItems();
  };

  const onCancel = () => {
    setEditing(false);
    setCurrentForm(props.documentControlForm.initializeItemForm());
    getItems();
    setIsBlurTemporality(false);
  };

  const getItems = () => {
    let items = props.documentControlForm.getValue().map((item) => {
      const from = item.getFromDate();
      const to = item.getLimitDate()
      const temporality = item.getTemporality()
      return {
        uploaded: item.isUploaded(),
        expired: item.isExpired(),
        concept: item.getConcept(),
        documentTypeName: item.getDocumentType().getName(),
        limitDate: to?.toFormat('dd/LL/yyyy') ?? 'Por calcular',
        responsible: item.getCollaborator().fullName(),
        fromDate: from?.toFormat('dd/LL/yyyy') ?? 'Por calcular',
        temporality: getPeriodNameInRange(from, to, temporality),
        typeTemporality: temporality?.dictemp_nombre
      };
    });

    setItems(items);
  };

  const formatUploaded = (item: any) => {
    if (item.uploaded) {
      return <Icon iconName="CheckMark" className="success-color" />;
    }
    if (item.expired) {
      return <Icon iconName="AlertSolid" className="error-color" />;
    } else {
      return <Icon iconName="EaseOfAccess" className="" />;
    }
  };

  const formatEquipo = (item: any) => {
    var getInitials = function (string: string) {
      var names = string.split(' '),
          initials = names[0].substring(0, 1).toUpperCase();

      if (names.length > 1) {
        initials += names[1].substring(0, 1).toUpperCase();
      }
      return initials;
    };
    return (
        <Persona
            text={item.responsible}
            size={PersonaSize.size24}
            imageInitials={getInitials(item.responsible)}
        />
    );
  };

  const selectRow = (idx: number) => {};

  const _onRenderRow: IDetailsListProps['onRenderRow'] = (propsDr) => {
    if (propsDr) {
      return (
          <div
              onClick={() => {
                const docControl = props.documentControlForm.getValue()[propsDr.itemIndex];
                onEditing(docControl);
                selectRow(propsDr.itemIndex);
              }}
          >
            <DetailsRow {...propsDr} />
          </div>
      );
    }
    return null;
  };

  const columns: IColumn[] = React.useMemo(
      () => {
        const baseColumns: IColumn[] = [
          {
            key: 'uploaded',
            name: t('Estado'),
            fieldName: 'uploaded',
            minWidth: 50,
            maxWidth: 50,
            className: 'fs-for-small-pill mt-2px',
            onRender: (item: any) => formatUploaded(item),
          },

          {
            key: 'concept',
            name: t('Concepto'),
            fieldName: 'concept',
            minWidth: 100,
            maxWidth: 100,
            className: 'fs-for-small-pill mt-2px',
          },
          {
            key: 'documentTypeName',
            name: t('Tipo de documento'),
            fieldName: 'documentTypeName',
            minWidth: 100,
            maxWidth: 100,
            className: 'fs-for-small-pill mt-2px',
          },
          {
            key: 'limitDate',
            name: t('Fecha límite'),
            fieldName: 'limitDate',
            minWidth: 100,
            maxWidth: 100,
            className: 'fs-for-small-pill mt-2px',
          },
          {
            key: 'responsible',
            name: t('Responsable'),
            fieldName: 'responsible',
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: any) => formatEquipo(item),
          }
        ]

        if (isService) {
          const fromDateColumn = {
            key: 'fromDate',
            name: t('Fecha desde'),
            fieldName: 'fromDate',
            minWidth: 100,
            maxWidth: 100,
            className: 'fs-for-small-pill mt-2px',
          };
          
          const temporalityColumn = {
            key: 'temporality',
            name: t('Temporalidad'),
            fieldName: 'temporality',
            minWidth: 200,
            maxWidth: 200,
            className: 'fs-for-small-pill mt-2px',
          };

          const typeTemporalityColumn = {
            key: 'typeTemporality',
            name: t('Tipo de temporalidad'),
            fieldName: 'typeTemporality',
            minWidth: 120,
            maxWidth: 120,
            className: 'fs-for-small-pill mt-2px',
          };
          
          baseColumns.splice(3, 0, fromDateColumn);
          baseColumns.splice(6, 0, temporalityColumn);
          baseColumns.splice(7, 0, typeTemporalityColumn);
        }

        return baseColumns;
      },
      [items, isService]
  );

  const onBlurCustomInputForDocForm = (customInput: CustomInputIfc) => {
    props.onBlurCustomInput(customInput);
    const isValid = isService ? currentForm.isValid() && temporabilityInput.getValue() !== undefined : currentForm.isValid();
    setFormDocIsValid(isValid);
  };

  return (
      <div className="ms-Grid bg-white-h100 ms-depth-4" dir="ltr">
        <div className="ms-Grid-row padding-1">
          <div className="ms-Grid-col ms-sm12">
            <div className="ms-Grid-row ms-sm12">
                <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg6 ms-xl6 mb-2">
                  <Text variant={'large'} className={'project-config-section-title'}>
                    <Icon iconName="ProtectedDocument" className="text-icon" />
                    {t('Control de documentos obligatorios')}
                  </Text>
                </div >
                <div className='ms-Grid-row ms-sm12 '>
                {editing ? (
                    <div >
                      {documentControlSelected === undefined ? (
                          <PrimaryButton
                              text={t('Guardar')}
                              onClick={onSave}
                              disabled={!formDocIsValid}
                              iconProps={{ iconName: 'Save' }}
                              className="primary-button ml-1 float-left"
                          />
                      ) : (
                          <PrimaryButton
                              text={t('Editar')}
                              onClick={onSave}
                              disabled={!formDocIsValid}
                              iconProps={{ iconName: 'Save' }}
                              className="primary-button ml-1  float-left"
                          />
                      )}
                      <DefaultButton className="ml-1 float-left" text={t('Cancelar')} onClick={onCancel} />
                    </div>
                ) : (
                    <PrimaryButton
                        text={t('Agregar')}
                        onClick={() => onEditing(undefined)}
                        iconProps={{ iconName: 'Add' }}
                        className="primary-button ml-1 float-right"
                    />
                )}
              </div>
            </div>

            {editing && (
                <div className="ms-Grid-row mt-2">
                  <div className="ms-Grid-col ms-sm6 ms-md3 ms-lg3 ms-xl3 ">
                    <DropdownInput
                        input={currentForm.getDocumentTypeInput()}
                        onBlur={onBlurCustomInputForDocForm}
                        label={t('Tipo de documento')}
                        options={props.documentTypeList}
                        mapFn={(documentType) => {
                          let ret: IDropdownOption = {
                            key: documentType.getId(),
                            text: documentType.getName(),
                          };
                          return ret;
                        }}
                    />
                  </div>
                  <>
                    <div className="ms-Grid-col ms-sm6 ms-md3 ms-lg3 ms-xl3 ">
                      <DropdownInput
                          input={currentForm.getResponsibleInput()}
                          onBlur={onBlurCustomInputForDocForm}
                          label={t('Responsable')}
                          options={props.collaboratorsInTeam.map((item) => item.getCollaborator())}
                          mapFn={(col) => {
                            let ret: IDropdownOption = {
                              key: col.getId(),
                              text: col.fullName(),
                            };
                            return ret;
                          }}
                      />
                    </div>

                    <div className="ms-Grid-col ms-sm6 ms-md3 ms-lg3 ms-xl3 ">
                      <InputField
                          input={currentForm.getConceptInput()}
                          label={t('Concepto')}
                          onBlur={onBlurCustomInputForDocForm}
                      />
                    </div>
                      

                    {isService ? (
                       <div className="ms-Grid-col ms-sm6 ms-md3 ms-lg3 ms-xl3 ">
                        <DropdownInput
                            input={currentForm.getTemporalityInput()}
                            onBlur={(input) => {
                              onBlurCustomInputForDocForm(input);
                              setIsBlurTemporality(true);
                            }}
                            label={t('Temporalidad')}
                            options={props.temporalities}
                            mapFn={(temporality) => {
                              let ret: IDropdownOption = {
                                key: temporality.dictemp_id,
                                text: temporality.dictemp_nombre,
                              };
                              return ret;
                            }}

                        />

                        {(!temporabilityInput.getValue() && isBlurTemporality )&& (
                          <div className="d-grid">
                            <Text className="error-message" variant="medium">
                             {t("La temporalidad no puede ser vacio")}
                            </Text>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="ms-Grid-col ms-sm6 ms-md3 ms-lg3 ms-xl3 ">
                        <DatePickerInput
                            propsInput={{
                              minDate: props.formProject.getStartValue().toJSDate(),
                              maxDate: props.formProject.getEndValue().toJSDate(),
                            }}
                            input={currentForm.getLimitDateInput()}
                            onBlur={onBlurCustomInputForDocForm}
                            label={t('Fecha límite')}
                        />
                      </div>
                    )}
                  </>
                </div>
            )}

            <DetailsList
                className="mt-2"
                items={items}
                columns={columns}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.justified}
                isHeaderVisible={true}
                onRenderRow={_onRenderRow}
                onShouldVirtualize={() => false}
            />

            { isInvalidTemporability && (
              <div className="d-grid mt-2">
                <Text className="error-message" variant="medium">
                  {t("Hay documentos que requieren una temporalidad, por favor, seleccione una temporalidad para cada uno de ellos.")}
                </Text>
              </div>
            )}
          </div>

          <ModalCannotModifyDocs
              open={showCannotModifyDoc}
              onDimiss={() => setShowCannotModifyDoc(false)}
          ></ModalCannotModifyDocs>
        </div>
        <br/>
      </div>
  );
};
